import { Button } from '@goodboydigital/astro';
import { euclideanModulo } from '@goodboydigital/odie';
import { Container, InteractionEvent, Sprite, Texture } from 'pixi.js';

import { Shared } from '../App';
import Curve from '../utils/Curve';
import { Maths } from '../utils/Maths';
import { PaintIcon } from './PaintIcon';
import { RadioButton, RadioButtonOpts } from './RadioButton';
import { RadioSelector, RadioSelectorStyles } from './RadioSelector';
import { IconSizes } from './ShopIcon';

const defaultOpts = {
    buttons: [] as RadioButtonOpts[] | any[],
    buttonType: RadioButton || Button as any,
    padding: 2,
    multiSelect: false,
    style: RadioSelectorStyles.inline,
    inlineWrapLimit: 100,
    containerHeight: 50,
    cancelCallback: () => { /* */ },
    width: 300,
    height: 300,
};

export type RadioSelectorOpts = typeof defaultOpts;

export class PaintSelector
{
    private pressing: boolean;
    private dragged: boolean;
    private pointerY: number;
    private pointerVelY: number;
    private targetY: number;

    private snapStart: number;
    private snapEnd: number;
    private snapDuration: number;
    private snapTime: number;

    public view: Container;
    public radioSelector: RadioSelector;
    private touchArea: Sprite;

    constructor(opts: Partial<RadioSelectorOpts>)
    {
        const o = { ...defaultOpts, ...opts };

        this.view = new Container();

        this.touchArea = new Sprite(Texture.WHITE);
        this.touchArea.anchor.set(0.5);
        this.touchArea.width = o.width;
        this.touchArea.height = o.height;
        this.touchArea.alpha = 0;
        this.view.addChild(this.touchArea);

        this.radioSelector = new RadioSelector(o);
        this.view.addChild(this.radioSelector.view);

        const mask = new Sprite(Shared.assets.getTexture('gradient-alpha'));

        mask.anchor.set(0.5);
        mask.width = o.width;
        mask.height = o.height;
        this.view.addChild(mask);
        this.radioSelector.view.mask = mask;

        this.snapStart = 0;
        this.snapEnd = 0;
        this.snapDuration = 0;
        this.snapTime = 0;

        this.pressing = false;
        this.dragged = false;
        this.pointerY = 0;
        this.pointerVelY = 0;
        this.targetY = 0;

        this.touchArea.interactive = true;
        this.touchArea.on('pointerdown', this.onPointerDown.bind(this));
        this.touchArea.on('pointermove', this.onPointerMove.bind(this));
        this.touchArea.on('pointerup', this.onPointerUp.bind(this));
        this.touchArea.on('pointerupoutside', this.onPointerUp.bind(this));
        this.touchArea.on('pointercancel', this.onPointerUp.bind(this));

        for (let i = 0; i < this.radioSelector._buttons.length; i++)
        {
            const button = this.radioSelector._buttons[i];
            const meta = o.buttons[i];

            button.onPress.disconnectAll();
            button.onPress.connect(() =>
            {
                if (this.dragged) return;
                this.selectButton(button);
                meta.onClick();
            });
            button.view.on('pointerdown', this.onPointerDown.bind(this));
            button.view.on('pointerup', this.onPointerUp.bind(this));
            button.view.on('pointerupoutside', this.onPointerUp.bind(this));
            button.view.on('pointercancel', this.onPointerUp.bind(this));
        }
    }

    public moveUp(): void
    {
        this.snap(this.targetY - this.getRowHeight());
    }

    public moveDown(): void
    {
        this.snap(this.targetY + this.getRowHeight());
    }

    private onPointerDown(e: InteractionEvent): void
    {
        this.pressing = true;
        this.dragged = false;
        this.pointerY = e.data.global.y;
        this.pointerVelY = 0;
        this.snapTime = 0;
        this.snapDuration = 0;
    }

    private onPointerMove(e: InteractionEvent): void
    {
        if (!this.pressing) return;
        this.pointerVelY = e.data.global.y - this.pointerY;
        this.pointerY = e.data.global.y;
        if (Math.abs(this.pointerVelY) > 2 || this.dragged)
        {
            this.dragged = true;
            const limit = this.getContentHeight() / 2;
            let v = this.pointerVelY;

            if (this.targetY > limit || this.targetY < -limit) v *= 0.3;

            this.targetY += v;
        }
    }

    private onPointerUp(): void
    {
        if (!this.pressing) return;
        if (this.dragged) Shared.game.soundSystem.playSfx('slide');
        this.pressing = false;
        this.snap(this.targetY + (this.pointerVelY * 10) + (this.getRowHeight() / 2));
    }

    private clampToLimits(v: number): number
    {
        const height = this.getContentHeight();

        return Maths.clamp(v, -height / 2, height / 2);
    }

    public update(delta = 1): void
    {
        if (!this.pressing && this.snapDuration && this.snapTime <= this.snapDuration)
        {
            this.snapTime += delta * (1 / 60);

            if (this.snapTime >= this.snapDuration) this.snapTime = this.snapDuration;
            this.targetY = Maths.tween(this.snapStart, this.snapEnd, Curve.backOut(this.snapTime / this.snapDuration));

            if (this.snapTime === this.snapDuration)
            {
                this.snapTime = 0;
                this.snapDuration = 0;
            }
        }

        this.radioSelector.view.y = this.targetY;
    }
    
    private selectButton(button: RadioButton): void
    {
        if (this.dragged) return;
        button.state = !button['_active'];
        this.radioSelector.setActive(this.radioSelector._buttons.indexOf(button));
        this.snapToButton(button);
        Shared.game.soundSystem.playSfx('paint', 1);
    }

    private getRowHeight(): number
    {
        return IconSizes[Shared.settings.shopIconSize] + this.radioSelector['_padding'];
    }

    private getContentHeight(): number
    {
        const diff = this.radioSelector._buttons.length % 2;

        return (this.radioSelector._buttons.length - diff) * 0.5 * this.getRowHeight();
    }

    private snap(end: number, animated = true): void
    {
        const diff = euclideanModulo(end, this.getRowHeight());

        this.snapStart = this.targetY;
        this.snapEnd = Math.round(this.clampToLimits(end - diff));
        this.snapDuration = animated ? Maths.clamp(Math.abs(this.snapEnd - this.snapStart) * 0.001, 0.3, 1.3) : 0.01;
    }

    private snapToButton(button: RadioButton, animated = true): void
    {
        this.snap(-button.view.y + (this.getContentHeight() / 2), animated);
    }

    public snapToSelected(animated = true): void
    {
        for (const button of this.radioSelector._buttons)
        {
            if ((button as unknown as PaintIcon).selected)
            {
                this.snapToButton(button, animated);

                return;
            }
        }
    }
}
